import React from "react";
import { Divider } from "@mui/material";
import Grid from '@mui/material/Grid2';
import { useSnackbar } from "notistack";

// Zustand store
import { useAppStore } from "../../stores";
import { saveConvention, fetchSpecifiedConvention } from "./services";
import GenericTabs from "components/GenericTabs/GenericTabs";
import ConventionManagementCreateEditForm from "./ConventionManagementCreateEditForm";
import GenericSpinner from "components/GenericSpinner/GenericSpinner";
import CommitteeCapacityModification from "./CommitteeCapacityModification";

interface Props {}

const ConventionManagement: React.FC<Props> = (props) => {
	const { enqueueSnackbar } = useSnackbar();

	// Zustand store
	const activeConvention = useAppStore((state) => state.conventionReducer?.activeConvention);
	const setActiveConvention = useAppStore((state) => state.setActiveConvention);

	const [onLoadPending, setOnLoadPending] = React.useState(false);
	const [savePending, setSavePending] = React.useState(false);

	const [tabIndex, setTabIndex] = React.useState(0);

	const [activeConventionInComponent, setActiveConventionInComponent] =
		React.useState({
			conventionId: 0,
			title: "",
			tagline: "",
			hashtag: "",
			location: "",
			streetAddress: "",
			city: "",
			state: "",
			zipcode: "",
			startDate: "",
			endDate: "",
			postmarkDate: "",
			defaultPasskeyEventCode: "",
			active: false,
			passkeyEventId: 0,
			conventionUrl: "",
			conventionCventMapping: null as unknown
		});

	const [committees, setCommittees] = React.useState<{ name: string; value: string }[]>([]);

	const formatCommitteesForDropdown = (committees: any) => {
		return [{ name: "All Committees", value: "" }].concat(committees);
	};

	React.useEffect(() => {
		if (!activeConvention?.conventionId) {
			setOnLoadPending(false);
			return;
		}
		setOnLoadPending(true);
		fetchSpecifiedConvention(activeConvention.conventionId)
			.then((response: any) => {
				if (response && response.data) {
					const { data } = response;
					setActiveConventionInComponent(data.convention);
					if (data.committees.length < 1) {
						enqueueSnackbar("No committees are available for this convention!", {
							variant: "error",
						});
					} else {
						let formattedCommittees = formatCommitteesForDropdown(
							data.committees
						);
						setCommittees(formattedCommittees);
					}
				}
				setOnLoadPending(false);
			})
			.catch((error) => {
				console.error("Error fetching convention:", error);
				enqueueSnackbar("Error loading convention data", { variant: "error" });
				setOnLoadPending(false);
			});
	}, [activeConvention?.conventionId]);

	const [isCventIdSet, setIsCventIdSet] = React.useState(false);

	// Validation for the form.
	const [errors, setErrors] = React.useState({
		zipcode: { error: false },
		capacity: { error: false },
		passkeyEventId: { error: false },
	});

	const onChange = (e: any) => {
		// validation
		const { name, value, checked, type } = e.target;

	//	 if (type === "checkbox") {
	//		console.log(e);
	//		if(name == "active")
	//		{
	//			console.log(e.target)
	//		}
	//	   newActiveConventionObj[name] = checked;

	//	  setActiveConventionInComponent({ ...newActiveConventionObj });
	//	  return;
//		 }

		let errorExists = false;
		let pattern;

		if (name === "zipcode") {
			pattern = /^\d{5}$/g;
		} else if (name === "capacity") {
			pattern = /^-?\d*\.?\d*$/;
		} else if (name === "passkeyEventId") {
			pattern = /^\d+$/;
		} else {
			return;
		}

		if (!value.match(pattern) && value.length > 0) {
			errorExists = true;
		}

		setErrors({
			...errors,
			[name]: { error: errorExists },
		});
	};

	const formatActiveConvention = (e: any) => {
		const {
			title,
			tagline,
			hashtag,
			location,
			streetAddress,
			city,
			state,
			zipcode,
			startDate,
			endDate,
			postmarkDate,
			defaultPasskeyEventCode,
			passkeyEventId,
			conventionUrl,
			eventId,
			eventAdmissionItemId,
			memberIdCustomFieldId,
			localNameCustomFieldId,
			localNumberCustomFieldId,
			delegateBadgeNumberCustomFieldId,
			delegateLocalNumberCustomFieldId,
			delegateLocalNameCustomFieldId,
			committeeChoice1CustomFieldId,
			committeeChoice2CustomFieldId,
			committeeChoice3CustomFieldId,
			alternativePriorityCustomFieldId,
			badgeNumberCustomFieldId,
			stateCustomFieldId,
			regionCustomFieldId
		} = e.target;

    console.log("Target:");
    console.log(e.target);

		return {
			title: title.value,
			tagline: tagline.value,
			hashtag: hashtag.value,
			location: location.value,
			streetAddress: streetAddress.value,
			city: city.value,
			state: state.value,
			zipcode: zipcode.value,
			startDate: startDate.value,
			endDate: endDate.value,
			postmarkDate: postmarkDate.value,
			defaultPasskeyEventCode: defaultPasskeyEventCode.value,
			passkeyEventId: passkeyEventId.value,
			conventionUrl: conventionUrl.value,
			conventionCventMapping: {
				eventId: eventId.value,
				eventAdmissionItemId: eventAdmissionItemId.value,
				memberIdCustomFieldId: memberIdCustomFieldId.value,
				localNameCustomFieldId: localNameCustomFieldId.value,
				localNumberCustomFieldId: localNumberCustomFieldId.value,
				delegateLocalNameCustomFieldId: delegateLocalNameCustomFieldId.value,
				delegateBadgeNumberCustomFieldId: delegateBadgeNumberCustomFieldId.value,
				delegateLocalNumberCustomFieldId: delegateLocalNumberCustomFieldId.value,
				alternativePriorityCustomFieldId: alternativePriorityCustomFieldId.value,
				committeeChoice1CustomFieldId: committeeChoice1CustomFieldId.value,
				committeeChoice2CustomFieldId: committeeChoice2CustomFieldId.value,
				committeeChoice3CustomFieldId: committeeChoice3CustomFieldId.value,
				badgeNumberCustomFieldId: badgeNumberCustomFieldId.value,
				stateCustomFieldId: stateCustomFieldId.value,
				regionCustomFieldId: regionCustomFieldId.value
			}
		};
	};

	const conventionCreationOnSubmit = (e: React.SyntheticEvent<EventTarget>) => {
		e.preventDefault();
		setSavePending(true);
		console.log("event: ", e);

		const activeConventionInformation = formatActiveConvention(e);

		saveConvention({
			...activeConventionInformation,
			ConventionId: tabIndex === 0 ? activeConvention.conventionId : 0,
			Active: tabIndex === 0 ? activeConventionInComponent.active : false,
		})
			.then(function (response) {
				if (response && response.data) {
					const { data } = response;
					if (tabIndex === 0) {
						setActiveConvention(data);
						setActiveConventionInComponent(data);
					}
					enqueueSnackbar(
						`Convention ${tabIndex === 0 ? "updated" : "created"}!`,
						{ variant: "success" }
					);
				}
				setSavePending(false);
			})
			.catch((error) => {
				enqueueSnackbar("error: " + error, { variant: "error" });
				setSavePending(false);
			});
	};

	const tabsOnChange = (_event: React.SyntheticEvent, newValue: number) => {
		setTabIndex(newValue);
	};

	return (
		<Grid container size={{ xs: 12 }}>
			<Grid size={{ xs: 12 }}>
				<h2>Convention Management</h2>
			</Grid>
			<Grid size={{ xs: 12 }} className="mb-5">
				<GenericTabs
					tabs={[
						{ name: "Edit Current Convention" },
						{ name: "Create New Convention" },
					]}
					onChange={tabsOnChange}
					tabContent={[
						{
							content:
								onLoadPending || savePending ? (
									<GenericSpinner
										includeOverlay={false}
										padding={0}
									/>
								) : (
									<Grid container size={{ xs: 12 }}>
										<Grid size={{ xs: 12 }}>
											<ConventionManagementCreateEditForm
												onSubmit={
													conventionCreationOnSubmit
												}
												currentConvention={
													activeConventionInComponent as any
												}
												onChangeForElements={onChange}
												errors={errors}
												isCventIdSet={isCventIdSet}
											/>
										</Grid>
										<Grid
											size={{ xs: 12 }}
											className="pt-4"
										>
											<Divider variant="middle" />
										</Grid>
										<Grid
											size={{ xs: 12 }}
											className="pt-4"
										>
											<CommitteeCapacityModification
												conventionId={
													activeConvention.conventionId
												}
												committees={committees}
												header={`Choose a committee to alter the capacity for the ${activeConventionInComponent.title}`}
												errors={errors}
												onChangeForElements={onChange}
											/>
										</Grid>
									</Grid>
								),
						},
						{
							content: savePending ? (
								<GenericSpinner
									includeOverlay={false}
									padding={0}
								/>
							) : (
								<ConventionManagementCreateEditForm
									onSubmit={conventionCreationOnSubmit}
									onChangeForElements={onChange}
									errors={errors}
								/>
							),
						},
					]}
				/>
			</Grid>
		</Grid>
	);
};

export default ConventionManagement;
